import { createRouter as createVueRouter, createWebHistory } from 'vue-router'
import Portal from '../views/portal/portal.vue'
import Login from '../views/login/login.vue'
import Dashboard from '@/views/portal/dashboard/dashboard.vue'
import Documents from '@/views/portal/documents/documents.vue'
import Auth from '@/views/login/auth/auth.vue'
import CreatePassword from '@/views/login/create-password/create-password.vue'
import LoginSupport from '@/views/login/support/support.vue'
import MyEnvironment from '@/views/portal/my-environment/my-environment.vue'
import Notifications from '@/views/portal/my-environment/notifications/notifications.vue'
import Users from '@/views/portal/my-environment/users/users.vue'
import Companies from '@/views/portal/companies/companies.vue'
import Profile from '@/views/portal/my-environment/profile/profile.vue'
import RequestPassword from '@/views/login/request-password/request-password.vue'
import PortalSupport from '@/views/portal/my-environment/support/support.vue'
import Impersonate from '@/views/login/impersonate/impersonate.vue'
import SetCompany from '@/views/login/set-company/set-company.vue'
import store from '@/store'
import { Permissions } from '@/classes/generated-models'
import UserManagement from '@/views/portal/user-management/user-management.vue'
import { hasPermissionCheck } from '@/utils/permission'
import { LocalStorageService } from '@/localStorageService'
import { PageNames } from '@/classes/types'
import { translate } from '@/i18n/service'
import { AppRoute, AppRouteRecord, AppRouteTarget, AppRouter, IAppRouteMeta } from '@/router/types'

const routes: AppRouteRecord[] = [
  {
    path: '/',
    name: 'portal',
    meta: { title: 'COMMON.PORTAL' },
    component: Portal,
    redirect: { name: PageNames.Login },
    children: [
      {
        path: 'dashboard',
        name: PageNames.Dashboard,
        component: Dashboard,
        meta: {
          title: 'MENU.DASHBOARD',
          permission: Permissions.DashboardPageView
        }
      },
      // { shipment-request is not yet implemented
      //   path: 'shipment-request',
      //   name: 'shipment-request',
      //   component: ShipmentRequest,
      //   meta: { title: 'MENU.SHIPMENT_REQUEST', permission: Permissions.ShipmentRequestPageView },
      // },
      {
        path: 'documents',
        name: PageNames.Documents,
        component: Documents,
        meta: {
          title: 'MENU.DOCUMENTS',
          permission: Permissions.DocumentPageView
        }
      },
      {
        path: 'support',
        name: PageNames.PortalSupport,
        component: PortalSupport,
        meta: { title: 'MY_ENVIRONMENT.SUPPORT' }
      },
      {
        path: 'user-management',
        name: PageNames.UserManagement,
        component: UserManagement,
        meta: {
          title: 'MENU.USER_MANAGEMENT',
          permission: Permissions.UserManagementPageView
        }
      },
      {
        path: 'companies',
        name: PageNames.Companies,
        component: Companies,
        meta: {
          title: 'COMPANIES.META_TITLE',
          permission: Permissions.UserManagementPageView
        }
      },
      {
        path: 'my-environment',
        name: PageNames.MyEnvironment,
        component: MyEnvironment,
        meta: { title: 'COMMON.MY_ENVIRONMENT' },
        children: [
          {
            path: 'profile',
            name: PageNames.Profile,
            component: Profile,
            meta: { title: 'MY_ENVIRONMENT.YOUR_PROFILE' }
          },
          {
            path: 'users',
            name: PageNames.Users,
            component: Users,
            meta: {
              title: 'MY_ENVIRONMENT.USERS',
              permission: Permissions.UserRead
            }
          },
          {
            path: 'notifications',
            name: PageNames.Notifications,
            component: Notifications,
            meta: {
              title: 'MY_ENVIRONMENT.NOTIFICATIONS',
              permission: Permissions.NotificationRead
            }
          }
        ]
      }
    ]
  },
  {
    path: '/login',
    name: 'login',
    meta: { title: 'LOGIN.LOGIN' },
    component: Login,
    redirect: { name: 'auth' },
    children: [
      {
        path: 'auth',
        name: 'auth',
        component: Auth
      },
      {
        path: 'create-password',
        name: PageNames.CreatePassword,
        component: CreatePassword,
        meta: { title: 'CREATE_PASSWORD.CREATE_PASSWORD' }
      },
      {
        path: 'impersonate',
        name: PageNames.Impersonate,
        component: Impersonate,
        meta: {
          title: 'COMMON.IMPERSONATE',
          permission: Permissions.Impersonate
        }
      },
      {
        path: 'request-password',
        name: PageNames.RequestPassword,
        component: RequestPassword,
        meta: { title: 'REQUEST_PASSWORD.REQUEST_PASSWORD' }
      },
      {
        path: 'support',
        name: PageNames.LoginSupport,
        component: LoginSupport,
        meta: { title: 'SUPPORT.TITLE' }
      },
      {
        path: 'set-company',
        name: PageNames.SetCompany,
        component: SetCompany,
        meta: {
          title: 'COMMON.IMPERSONATE',
          permission: Permissions.UserRead
        }
      }
    ]
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: { name: PageNames.Dashboard }
  }
]

function getRouteMeta(to: AppRoute): IAppRouteMeta {
  return (to.meta || {}) as IAppRouteMeta
}

function applyRouteTitle(to: AppRoute): void {
  const meta = getRouteMeta(to)
  if (meta.title) document.title = `${translate(meta.title)} - Roba Portal`
  else document.title = 'Roba Portal'
}

async function resolveNavigationTarget(to: AppRoute): Promise<void | AppRouteTarget> {
  const meta = getRouteMeta(to)

  if (store.state.Account.currentUser === undefined && LocalStorageService.getAccessToken()) {
    await store.dispatch('Account/fetchCurrentUser')
  }

  if (store.state.Account.currentUser && meta.permission && !hasPermissionCheck(store.state.Account.currentUser!, meta.permission)) {
    return { name: PageNames.Dashboard }
  }
}

function registerNavigationGuards(router: AppRouter): void {
  router.beforeEach(async (to: AppRoute) => {
    const target = await resolveNavigationTarget(to)
    return target
  })
}

function registerNavigationEffects(router: AppRouter): void {
  router.afterEach((to: AppRoute) => {
    window.scroll({ top: 0, left: 0 })
    applyRouteTitle(to)
  })
}

export function createRouter(): AppRouter {
  const router = createVueRouter({
    history: createWebHistory(process.env.BASE_URL || '/'),
    routes,
  })

  registerNavigationGuards(router)
  registerNavigationEffects(router)

  return router
}

export const router: AppRouter = createRouter()

export type { AppRouter } from '@/router/types'

export default router
